import { extend } from 'lodash'
import { useState } from 'react'
import { queryClient } from 'app/lib/queryClient'
import { getRequestQueryKey, runGetRequest } from 'app/lib/requestStrategy'
import { notifyError } from 'app/services/notify.client'
import { debug, logError } from 'lib/logging'
import { Jsonify } from 'lib/toJSON'

type RequestOptions = Omit<RequestInit, `body`> & { body: any }

/**
 * Make a request via window.fetch and return the response. All requests sent as JSON.
 * GET requests are routed through queryClient for deduplication and caching.
 *
 * @throws http responses with a status code >= 400
 */
export function useRequest<T>(showErrors = true) {
  const [inFlight, setInFlight] = useState(false)

  const fetchRequest = async (url: string, options: RequestInit): Promise<Jsonify<T> | undefined> => {
    setInFlight(true)
    let resp = await window.fetch(url, options)
    setInFlight(false)

    if (!resp.headers.get(`content-type`)?.match(`application/json`)) return

    let data: Jsonify<T> = await resp.json()

    if (resp.status >= 400) {
      logError(`Error response from server`, { status: resp.status, data })
      if (showErrors && resp.status !== 401) {
        const message =
          data?.[`error`] ?? `There was a problem loading data from the server. You may need to reload the page.`
        notifyError(message)
      }
    }

    return data
  }

  const request = async (url: string, opts?: RequestOptions) => {
    let options: RequestInit = {
      method: `GET`,
      ...opts,
      headers: {
        'Content-Type': `application/json`,
        ...opts?.headers,
      },
    }
    debug(`contact`, `request`, url, options)

    if (opts?.body && options.method === `GET`) {
      url += `?${new URLSearchParams(opts.body).toString()}`
      options.body = undefined
      debug(`contact`, `request`, `GET with body`, url, options)
    }

    if (typeof options?.body === `object`) {
      options.body = JSON.stringify(options.body)
    }

    const isGet = !opts?.method || opts.method === `GET`

    if (isGet) {
      return runGetRequest({
        cached: () =>
          queryClient.fetchQuery({
            queryKey: getRequestQueryKey(url),
            queryFn: () => fetchRequest(url, options),
          }),
        legacy: () => fetchRequest(url, options),
      })
    }

    return fetchRequest(url, options)
  }

  return extend(request, { inFlight })
}
